import logging from '../lib/logging';
import { isPlayNow, redirect, redirectHome } from '../lib/redirect';

import gtmDataLayerPush from '../lib/gtmDataLayerPush';
import {
	GtmEvens,
	GtmEventParameters,
	GtmFallbackVal,
	GtmThirdParty,
	prepareGtmPayload,
} from './gtm';

export default class HofCanvas {
	/**
	 * @type {HTMLIFrameElement|null}
	 */

	static canvas = document.getElementById('iframe_canvas');

	/**

	 * @type {HofUser|undefined}
	 */
	#user = undefined;

	/**
	 * @param {HofUser} user
	 */
	constructor(user) {
		this.#user = user;

		this.#postCanvasSize();
		this.#canvasMessage();
	}

	/**
	 * @param url
	 */
	#isWhitelistIframeDomains(url) {
		const domainData = parseDomain(url);

		if (!domainData) {
			return false;
		}

		const whitelistDomains = new Set([
			'playtika.com',
			'houseoffun.com',
			'houseoffuns.com',
		]);

		whitelistDomains.add(window.currentDomainData.domain);

		return whitelistDomains.has(domainData.domain);
	}

	/**
	 * @param {*} message
	 * @returns {void}
	 */
	static canvasPostMessage(message) {
		if (!message) {
			throw new Error('No message provided');
		}

		logging(
			'Canvas postMessage',
			() => {
				console.log(message);
			},
			'info'
		);

		HofCanvas.canvas?.contentWindow?.postMessage(message, '*');
	}

	#canvasMessage() {
		/** @type {Map<string, Function>} */
		const fnMap = new Map();

		fnMap.set('scrollTo', window.scrollTo);

		fnMap.set('playGameFromStore', redirect);
		fnMap.set('getOneAccessToken', () => {
			if (!this.#user.isLoggedIn) {
				HofCanvas.canvasPostMessage({
					login: {
						fb: 0,
						token: '',
					},
				});

				return;
			}

			if (this.#user.isFacebookUser) {
				HofCanvas.canvasPostMessage({
					login: {
						fb: 1,
						token: this.#user.fbAccessToken,
						connected: true,
					},
				});

				return;
			}

			if (this.#user.isOneUser) {
				HofCanvas.canvasPostMessage({
					login: {
						fb: 0,
						token: this.#user.oneAccessToken,
					},
				});
			}
		});

		fnMap.set('navigateToLanding', redirectHome);
		fnMap.set('preloaderFinished', (player_id, is_sign_up, is_first_login) => {
			if (!this.#user.isLoggedInFromSite) {
				return;
			}

			if (this.#user.isLobbyInitGtmSent) {
				return;
			}

			const isFbUser = this.#user.isFacebookUser;

			const isFirstTimeOnHofCom = Boolean(is_sign_up) || Boolean(is_first_login);

			const thirdPartyValue = (value) =>
				isFbUser ? value : GtmFallbackVal.EMPTY;

            const process_name = isFirstTimeOnHofCom ? 'register_success' : 'login_success';

            const login_source = isFbUser ? 'facebook' : 'oneconnect';

			gtmDataLayerPush(
				GtmEvens.FUNNEL_LOGIN,
				prepareGtmPayload({
					[GtmEventParameters.PROCESS_NAME]: process_name,
					[GtmEventParameters.PLAYER_ID]: player_id,
					[GtmEventParameters.IS_SIGN_UP]: isFbUser ? 0 : Number(isFirstTimeOnHofCom),
					[GtmEventParameters.THIRD_PARTY_SIGN_UP]: thirdPartyValue(Number(isFirstTimeOnHofCom)),
					[GtmEventParameters.THIRD_PARTY_LOGIN]: thirdPartyValue(Number(!isFirstTimeOnHofCom)),
					[GtmEventParameters.THIRD_PARTY_NAME]: thirdPartyValue(GtmThirdParty.FB),
                    [GtmEventParameters.LOGIN_SOURCE]: login_source,
				})
			);

			this.#user.setLobbyInitGtm();
		});

		fnMap.set('gtmDataLayerPush', gtmDataLayerPush);
		fnMap.set('lobbyLoaded', () => {
			gtmDataLayerPush('Landing on the Lobby');
		});

		const deprecatedFnList = [
			'setFbCookie',
			'setOneCookie',
			'clearCookies',
			'secondDayLogin',
			'purchaseMade',
			'spinCompleted',
			'conversionHOF_FTD',
			'conversionHOF_Purchase',
			'conversionHOF_Install',
			'hof_local_storage_get',
			'hof_local_storage_get',
		];

		deprecatedFnList.forEach((fnName) => {
			fnMap.set(fnName, (...params) => {
				logging(
					fnName,
					() => {
						console.warn(`Deprecated function: ${fnName}\n`, params);
					},
					'warning'
				);
			});
		});

		/**
		 * Event handler for the 'message' event.
		 *
		 * This function processes messages received from other windows or iframes.
		 * It checks the origin of the message to ensure it is from a trusted source,
		 * and if the conditions are met, it executes a function specified in the message data.
		 *
		 * @param {MessageEvent<TMessageEventDataType>} event - The message event object.
		 */
		const onMessage = ({ origin, data }) => {
			if (typeof data === 'undefined') {
				return;
			}

			const isValidOrigin = this.#isWhitelistIframeDomains(origin);

			if (!isValidOrigin) {
				return;
			}

			const isMaybeOneConnectGtmEvent =
				typeof data === 'string' && data.includes('OneConnect.gtmEvent');

			if (isMaybeOneConnectGtmEvent && !isPlayNow) {
				try {
					/**
					 * @type {TOneConnectMessageEventDataType}
					 */
					const message = JSON.parse(data);

					const isValidEventType =
						typeof message.event_type !== 'undefined' &&
						message.event_type === 'OneConnect.gtmEvent';

					if (!isValidEventType) {
						return;
					}

					gtmDataLayerPush(message.event, message.payload);
				} catch (e) {
					console.error(e);
				}
			} else {
				if (typeof data !== 'object') {
					return;
				}

				if (!data.func) {
					return;
				}

				const isFunction =
					typeof data.func === 'string' && fnMap.has(data.func);

				if (!isFunction) {
					throw new Error(`Unknown function: ${data.func}`);
				}

				if (!Array.isArray(data.params)) {
					throw new Error('params must be an array');
				}

				const fn = fnMap.get(data.func);

				logging(
					'Message',
					() => {
						console.log('function:', data.func);
						console.log('params:', data.params);
					},
					'info'
				);

				fn.apply(null, data.params);
			}
		};

		window.addEventListener('message', onMessage, false);
	}

	#postCanvasSize() {
		if (!HofCanvas.canvas) {
			return;
		}

		let savedW = window.innerWidth;
		let savedH = window.innerHeight;

		const getSize = () => {
			if (window.innerWidth !== savedW || window.innerHeight !== savedH) {
				savedW = window.innerWidth;
				savedH = window.innerHeight;
				HofCanvas.canvasPostMessage({ width: savedW, height: savedH });
			}
		};

		document.addEventListener('scroll', getSize);
		window.addEventListener('resize', getSize);

		HofCanvas.canvasPostMessage({ width: savedW, height: savedH });

		setTimeout(() => {
			HofCanvas.canvasPostMessage({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}, 5000);
	}
}
